import { Injectable, inject } from '@angular/core';
import { environment } from '@env/environment';
import {
  createRecaptchaHeaders,
  isRecaptchaTooLowScoreHttpResponse,
  RecaptchaData,
  TooLowRecaptchaScoreError,
} from '@app/core/recaptcha/recaptcha-commons';
import { Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { IdentifierType, MemberIdentifier } from '@app/core/_models/member-interface';
import { ClientSecret } from '@app/core/_models/client-secret';
import { MemberFormData, MemberValidationError, MemberValidationResponse } from '@app/core/members/interfaces';
import { HttpClient } from '@angular/common/http';
import { BaseParamsService } from '@app/core/base-params.service';

const getIdentifierUrlFragment = (memberIdentifier?: MemberIdentifier): string =>
  memberIdentifier?.identifier_type === IdentifierType.EMAIL ? 'by_email' : 'by_msisdn';

@Injectable()
export class MembersApiV3Service {
  private http = inject(HttpClient);
  private baseParamsService = inject(BaseParamsService);


  public sendPasswordByEmail(memberIdentifier?: MemberIdentifier): Observable<ClientSecret> {
    const identifierFragment = getIdentifierUrlFragment(memberIdentifier);
    return this.http.post<ClientSecret>(
      `${this.getBaseUrl()}/${identifierFragment}/${memberIdentifier?.identifier}/send_one_time_password_via_email`,
      {},
    );
  }

  public sendPasswordBySms(memberIdentifier: MemberIdentifier | undefined, recaptchaData: RecaptchaData): Observable<ClientSecret> {
    const identifierFragment = getIdentifierUrlFragment(memberIdentifier);
    return this.http.post<ClientSecret>(
      `${this.getBaseUrl()}/${identifierFragment}/${memberIdentifier?.identifier}/send_one_time_password_via_sms`,
      {},
      {
        headers: createRecaptchaHeaders(recaptchaData),
      },
    ).pipe(
      catchError(error => {
        if (isRecaptchaTooLowScoreHttpResponse(error)) {
          throw new TooLowRecaptchaScoreError();
        }
        throw error;
      }),
    );
  }


  public sendRegistrationPasswordByEmail(memberIdentifier: string | undefined): Observable<unknown> {
    return this.http.post(
      `${this.getBaseUrl()}/by_email/${memberIdentifier}/send_registration_password`, {},
    );
  }


  public validate(body: MemberFormData): Observable<boolean> {
    return this.http.post<MemberValidationResponse>(`${this.getBaseUrl()}/validate`, body)
      .pipe(
        map((response: MemberValidationResponse) => {
          if (response.valid) {
            return true;
          }
          throw new MemberValidationError(response.errors || {});
        }),
      );
  }

  public create(body: MemberFormData): Observable<unknown> {
    return this.http.post(this.getBaseUrl(), body);
  }

  private getBaseUrl(): string {
    return `${environment.webforms_api}/v3/${this.baseParamsService.slug}/members`;
  }
}
